import { LRUCache } from 'lru-cache';

// Create cache instance
const cache = new LRUCache({
  max: 100, // Maximum 100 items in cache
  ttl: 1000 * 60 * 5, // 5 minutes default TTL
  updateAgeOnGet: true, // Refresh TTL when item is accessed
  allowStale: false, // Don't return stale items
  noDisposeOnSet: true // Don't dispose items when setting
});

// CacheManager object with methods
const CacheManager = {
  /**
   * Set a value in cache with optional TTL
   * @param {string} key - Cache key
   * @param {any} data - Data to cache
   * @param {number} ttl - Time to live in milliseconds (optional, uses default if not provided)
   */
  set(key, data, ttl) {
    try {
      cache.set(key, data, { ttl: ttl || 1000 * 60 * 5 });
    } catch (error) {
      console.error('Error setting cache:', error);
    }
  },

  /**
   * Get a value from cache
   * @param {string} key - Cache key
   * @returns {any} Cached data or null if not found/expired
   */
  get(key) {
    try {
      return cache.get(key);
    } catch (error) {
      console.error('Error getting cache:', error);
      return null;
    }
  },

  /**
   * Delete a specific key from cache
   * @param {string} key - Cache key to delete
   */
  delete(key) {
    try {
      cache.delete(key);
    } catch (error) {
      console.error('Error deleting cache:', error);
    }
  },

  /**
   * Clear all items from cache
   */
  clear() {
    try {
      console.log('CacheManager: Clearing all caches...');
      const sizeBefore = cache.size;
      cache.clear();
      console.log(`CacheManager: Cleared ${sizeBefore} items from cache`);
    } catch (error) {
      console.error('Error clearing cache:', error);
    }
  },

  /**
   * Get cache statistics
   * @returns {Object} Cache statistics
   */
  getStats() {
    try {
      return cache.getStats();
    } catch (error) {
      console.error('Error getting cache stats:', error);
      return {};
    }
  },

  /**
   * Check if a key exists in cache
   * @param {string} key - Cache key to check
   * @returns {boolean} True if key exists
   */
  has(key) {
    try {
      return cache.has(key);
    } catch (error) {
      console.error('Error checking cache:', error);
      return false;
    }
  },

  /**
   * Get cache size (number of items)
   * @returns {number} Number of items in cache
   */
  size() {
    try {
      return cache.size;
    } catch (error) {
      console.error('Error getting cache size:', error);
      return 0;
    }
  },

  /**
   * Get all cache keys
   * @returns {Array} Array of cache keys
   */
  keys() {
    try {
      return cache.keys();
    } catch (error) {
      console.error('Error getting cache keys:', error);
      return [];
    }
  }
};

export default CacheManager; 